import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Plus, Search } from 'lucide-react';
import { useAdminEmailFlowsQuery } from '@/generated/graphql';
import Layout from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';

const PAGE_SIZE = 20;

const AdminEmailFlows = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  const { data, loading } = useAdminEmailFlowsQuery({
    variables: { page, pageSize: PAGE_SIZE, search },
    fetchPolicy: 'cache-and-network',
  });

  const flows = data?.adminEmailFlows?.data ?? [];
  const total = data?.adminEmailFlows?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <Layout>
      <main className="flex-1 p-4 sm:p-6 lg:p-8 overflow-y-auto">
        <div className="max-w-7xl mx-auto space-y-6">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-semibold">{t('admin.emailFlows.title')}</h1>
        <Button onClick={() => navigate('/admin/email-flows/new')}>
          <Plus className="mr-2 h-4 w-4" /> {t('admin.emailFlows.new')}
        </Button>
      </div>

      <Card className="mb-4">
        <CardContent className="p-3">
          <div className="relative">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder={t('admin.emailFlows.search')}
              className="pl-8"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>{flows.length} / {total}</CardTitle></CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('admin.emailFlows.fields.name')}</TableHead>
                <TableHead>{t('admin.emailFlows.fields.slug')}</TableHead>
                <TableHead>{t('admin.emailFlows.fields.trigger')}</TableHead>
                <TableHead>{t('admin.emailFlows.table.steps')}</TableHead>
                <TableHead>{t('admin.emailFlows.fields.isActive')}</TableHead>
                <TableHead>{t('admin.emailFlows.fields.isTransactional')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {flows.map((f) => (
                <TableRow
                  key={f.id}
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => navigate(`/admin/email-flows/${f.id}`)}
                >
                  <TableCell className="font-medium">{f.name}</TableCell>
                  <TableCell className="font-mono text-sm">{f.slug}</TableCell>
                  <TableCell>{f.trigger}</TableCell>
                  <TableCell>{f.stepsCount}</TableCell>
                  <TableCell>{f.is_active ? <Badge>Active</Badge> : <Badge variant="secondary">Off</Badge>}</TableCell>
                  <TableCell>{f.is_transactional ? <Badge variant="outline">Transactional</Badge> : null}</TableCell>
                </TableRow>
              ))}
              {!loading && flows.length === 0 && (
                <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground">—</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
          <div className="flex items-center justify-end gap-2 mt-3">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>{'<'}</Button>
            <span className="text-sm">{page} / {totalPages}</span>
            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(page + 1)}>{'>'}</Button>
          </div>
        </CardContent>
      </Card>
        </div>
      </main>
    </Layout>
  );
};

export default AdminEmailFlows;
